import { useEffect, useRef } from 'react';
import { Button, Input, Toggle } from '@playerbase/ui';

import { SELECT_OPTION_MAX } from '../../../../games/use-form-builder-state.js';

interface SelectConfigProps {
  options: string[];
  maxValues: number | undefined;
  onReplace: (index: number, value: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onMaxValuesChange: (maxValues: number | undefined) => void;
}

// Discord's String Select caps at 25 options. The reducer enforces both
// ends. The "Allow multiple selections" toggle flips the field between
// single-select (max=1, the default) and multi-select; the number input
// controls the cap, clamped to the option count by the reducer.
export function SelectConfig({
  options,
  maxValues,
  onReplace,
  onAdd,
  onRemove,
  onMaxValuesChange,
}: SelectConfigProps): JSX.Element {
  const atCap = options.length >= SELECT_OPTION_MAX;
  const multi = maxValues !== undefined && maxValues > 1;
  const ceiling = Math.min(options.length, SELECT_OPTION_MAX);

  // Focus the freshly-added option when the studio adds via Enter so they
  // can keep typing without reaching for the mouse. We track which index
  // wants focus on next render rather than calling .focus() inline because
  // the new input doesn't exist until the parent state has propagated.
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const focusIndexRef = useRef<number | null>(null);
  useEffect(() => {
    if (focusIndexRef.current === null) return;
    const target = inputsRef.current[focusIndexRef.current];
    if (target !== null && target !== undefined) target.focus();
    focusIndexRef.current = null;
  });

  function handleAdd(focusIndex?: number): void {
    if (atCap) return;
    if (focusIndex !== undefined) focusIndexRef.current = focusIndex;
    onAdd();
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-text-muted">Options</span>
        <ul className="flex flex-col gap-2">
          {options.map((option, index) => (
            <li key={index} className="flex items-center gap-2">
              <Input
                ref={(el) => {
                  inputsRef.current[index] = el;
                }}
                value={option}
                onChange={(e) => onReplace(index, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  // Enter on the last row appends a fresh option; Enter on
                  // any earlier row jumps focus to the next existing one
                  // so a studio can move down the list without leaving
                  // the keyboard.
                  if (index === options.length - 1) {
                    handleAdd(options.length);
                    return;
                  }
                  const next = inputsRef.current[index + 1];
                  if (next !== null && next !== undefined) next.focus();
                }}
                maxLength={100}
                placeholder={`Option ${index + 1}`}
                aria-label={`Option ${index + 1}`}
              />
              <Button
                variant="ghost"
                type="button"
                onClick={() => onRemove(index)}
                aria-label={`Remove option ${index + 1}`}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
        <div>
          <Button
            variant="ghost"
            type="button"
            onClick={() => handleAdd()}
            disabled={atCap}
          >
            {atCap ? `Maximum ${SELECT_OPTION_MAX} options` : 'Add option'}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-md border border-border bg-surface px-3 py-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-text">Allow multiple selections</span>
          <Toggle
            checked={multi}
            onCheckedChange={(checked) => {
              if (!checked) {
                onMaxValuesChange(undefined);
                return;
              }
              // Default to letting the player pick everything when they
              // first turn this on; the studio can dial it down via the
              // input below. Capped at the option count + Discord's 25.
              onMaxValuesChange(ceiling > 1 ? ceiling : 2);
            }}
            aria-label="Allow multiple selections"
            disabled={options.length < 2}
          />
        </div>
        {multi ? (
          <label className="flex items-center justify-between text-xs text-text-muted">
            <span>Maximum picks</span>
            <input
              type="number"
              min={2}
              max={ceiling}
              value={maxValues}
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                if (Number.isNaN(n)) return;
                onMaxValuesChange(n);
              }}
              className="w-20 rounded-md border border-border bg-surface-2 px-2 py-1 text-right text-sm text-text"
              aria-label="Maximum selections"
            />
          </label>
        ) : (
          <p className="text-xs text-text-muted">
            Players pick a single option. Turn this on to let them pick more than one.
          </p>
        )}
      </div>
    </div>
  );
}
